import React, { useEffect } from 'react';

// Performance monitoring utilities

// Measure component render time
export const measurePerformance = (componentName) => {
  const start = performance.now();
  
  return () => {
    const end = performance.now();
    const duration = end - start;
    
    
    // Send to analytics in production
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'component_render', {
        component_name: componentName,
        render_time: duration
      });
    }
    
    return duration;
  };
};

// Measure route transition time
export const measureRouteTransition = (fromRoute, toRoute) => {
  const start = performance.now();
  
  return () => {
    const end = performance.now();
    const duration = end - start;
    
    
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'route_transition', {
        from_route: fromRoute,
        to_route: toRoute,
        transition_time: duration
      });
    }
    
    return duration;
  };
};

// Measure bundle loading time
export const measureBundleLoad = (bundleName) => {
  const start = performance.now();
  
  return () => {
    const end = performance.now();
    const duration = end - start;
    
    
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'bundle_load', {
        bundle_name: bundleName,
        load_time: duration
      });
    }
    
    return duration;
  };
};

// Monitor memory usage
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = performance.memory;
    
    
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'memory_usage', {
        used_heap: memory.usedJSHeapSize,
        total_heap: memory.totalJSHeapSize,
        heap_limit: memory.jsHeapSizeLimit
      });
    }
  }
};

// Monitor network performance
export const monitorNetworkPerformance = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    
    
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'network_performance', {
        effective_type: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      });
    }
  }
};

// Performance observer for long tasks
export const observeLongTasks = () => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration > 50) { // Tasks longer than 50ms
          
          if (process.env.NODE_ENV === 'production' && window.gtag) {
            window.gtag('event', 'long_task', {
              duration: entry.duration,
              start_time: entry.startTime
            });
          }
        }
      });
    });
    
    observer.observe({ entryTypes: ['longtask'] });
  }
};

// Initialize performance monitoring
export const initPerformanceMonitoring = () => {
  // Monitor memory usage every 30 seconds
  setInterval(monitorMemoryUsage, 30000);
  
  // Monitor network performance every minute
  setInterval(monitorNetworkPerformance, 60000);
  
  // Start observing long tasks
  observeLongTasks();
  
  // Monitor initial page load using modern API
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const loadTime = performance.now();
      
      
      if (process.env.NODE_ENV === 'production' && window.gtag) {
        window.gtag('event', 'page_load', {
          load_time: loadTime
        });
      }
    });
  } else {
    // Page already loaded
    const loadTime = performance.now();
    
    
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', 'page_load', {
        load_time: loadTime
      });
    }
  }
};

// Higher-order component for performance monitoring
export const withPerformanceMonitoring = (WrappedComponent, componentName) => {
  return function PerformanceMonitoredComponent(props) {
    const measureRender = measurePerformance(componentName);
    
    useEffect(() => {
      const cleanup = measureRender();
      return cleanup;
    });
    
    return <WrappedComponent {...props} />;
  };
}; 
